const normalizeRoute = (route) => {
  if (typeof route !== "string") return "";
  return route.trim();
};

export const flattenEdgeMenuItems = (items) => {
  if (!Array.isArray(items)) return [];

  const flatItems = [];
  const visit = (menuItems) => {
    menuItems.forEach((item) => {
      if (!item || typeof item !== "object") return;
      flatItems.push(item);
      if (Array.isArray(item.children) && item.children.length > 0) {
        visit(item.children);
      }
    });
  };

  visit(items);
  return flatItems;
};

const EDGE_MENU_SECTION_ROUTES = {
  pos: ["/pos"],
  floorplan: ["/floor"],
  catalog: ["/catalog/items", "/catalog/categories", "/catalog/modifiers"],
  userManagement: ["/admin/users", "/admin/access-levels"],
  fulfillment: ["/orders/live", "/orders/history", "/cash-drawer", "/day-close"],
  devices: [
    "/config/devices",
    "/config/printers",
    "/config/kds",
    "/config/kiosk",
    "/config/payments",
  ],
  reporting: ["/reports/sales", "/reports/financial"],
  tillManagement: ["/cash-drawer", "/day-close"],
  settings: [
    "/pricing/tax",
    "/config/printers",
    "/config/kds",
    "/config/kiosk",
    "/config/payments",
  ],
};

const EDGE_MENU_ROUTE_RULES = [
  { frontendPath: "/pos", backendRoutes: ["/pos"], prefixMatch: true },
  { frontendPath: "/floorplan/floors", backendRoutes: ["/floor"] },
  { frontendPath: "/floorplan/sections", backendRoutes: ["/floor"] },
  { frontendPath: "/floorplan/tables", backendRoutes: ["/floor"] },
  { frontendPath: "/menu/categories", backendRoutes: ["/catalog/categories"] },
  {
    frontendPath: "/menu/items",
    backendRoutes: ["/catalog/items"],
    prefixMatch: true,
  },
  { frontendPath: "/menu/stations", backendRoutes: ["/catalog/modifiers"] },
  {
    frontendPath: "/menu/station-assignment",
    backendRoutes: ["/catalog/modifiers"],
  },
  { frontendPath: "/users", backendRoutes: ["/admin/users"], prefixMatch: true },
  {
    frontendPath: "/users/access-levels",
    backendRoutes: ["/admin/access-levels"],
    prefixMatch: true,
  },
  {
    frontendPath: "/devices/health",
    backendRoutes: ["/config/devices"],
  },
  {
    frontendPath: "/devices/local-devices",
    backendRoutes: ["/config/devices"],
  },
  {
    frontendPath: "/devices/kiosks",
    backendRoutes: ["/config/kiosk"],
  },
  {
    frontendPath: "/devices/terminals",
    backendRoutes: ["/config/devices"],
  },
  {
    frontendPath: "/reporting/orders",
    backendRoutes: ["/reports/sales", "/reports/financial", "/orders/history"],
    prefixMatch: true,
  },
  {
    frontendPath: "/reporting/items",
    backendRoutes: ["/reports/sales", "/reports/financial"],
    prefixMatch: true,
  },
  {
    frontendPath: "/cash-management/tills",
    backendRoutes: ["/cash-drawer", "/day-close"],
    prefixMatch: true,
  },
  {
    frontendPath: "/cash-management/till-sessions",
    backendRoutes: ["/cash-drawer", "/day-close"],
    prefixMatch: true,
  },
  {
    frontendPath: "/settings/configure-store",
    backendRoutes: ["/config/kiosk", "/config/payments"],
  },
  {
    frontendPath: "/settings/tax-rates",
    backendRoutes: ["/pricing/tax"],
    prefixMatch: true,
  },
  {
    frontendPath: "/settings/printers",
    backendRoutes: ["/config/printers"],
    prefixMatch: true,
  },
  {
    frontendPath: "/settings/fulfillment",
    backendRoutes: ["/config/kds", "/config/payments"],
    prefixMatch: true,
  },
  {
    frontendPath: "/settings/faq",
    backendRoutes: ["/config/kiosk", "/config/payments"],
    prefixMatch: true,
  },
  {
    frontendPath: "/reporting/order-lifecycle",
    backendRoutes: ["/orders/history", "/reports/sales", "/reports/financial"],
    prefixMatch: true,
  },
];

const collectBackendRoutes = (items) => {
  return flattenEdgeMenuItems(items)
    .map((item) => normalizeRoute(item?.route))
    .filter(Boolean);
};

const hasAnyRoute = (availableRoutes, requiredRoutes) =>
  requiredRoutes.some((route) => availableRoutes.has(route));

export const buildEdgeMenuAccess = (menuItems) => {
  const backendRoutes = new Set(collectBackendRoutes(menuItems));

  const canAccessBackendRoutes = (requiredRoutes = []) =>
    Array.isArray(requiredRoutes) &&
    requiredRoutes.length > 0 &&
    hasAnyRoute(backendRoutes, requiredRoutes);

  const canAccessSection = (sectionKey) =>
    canAccessBackendRoutes(EDGE_MENU_SECTION_ROUTES[sectionKey] || []);

  const canAccessFrontendRoute = (frontendPath) => {
    const normalizedPath = normalizeRoute(frontendPath);
    if (!normalizedPath) return true;

    const rule = EDGE_MENU_ROUTE_RULES.find((candidate) => {
      if (candidate.frontendPath === normalizedPath) return true;
      if (!candidate.prefixMatch) return false;
      return normalizedPath.startsWith(`${candidate.frontendPath}/`);
    });

    if (!rule) return true;
    return canAccessBackendRoutes(rule.backendRoutes);
  };

  return {
    backendRoutes,
    canAccessBackendRoutes,
    canAccessSection,
    canAccessFrontendRoute,
  };
};
